import { money, ApiRequestError } from '../../../lib/api';
import type { HealthScore } from '../../../shared/types';
import type { Resource } from '../useResource';
import { band } from '../homeData';
import { Card, CardTitle, Empty, ErrorState, LoadingRows, Pill } from '../ui';

/**
 * The household health score. PRD 8 and CONTRACT rule 6: illustrative, never
 * advice — so the number is always shown with the inputs that produced it.
 */
export default function HealthCard({ res }: { res: Resource<HealthScore> }) {
  const { data, loading, error, reload } = res;

  // Nothing to score yet is an empty state, not a failure.
  const notComputedYet = error instanceof ApiRequestError && error.status === 404;

  return (
    <Card className="flex flex-col p-5 sm:p-6">
      <CardTitle icon="pulse" action={{ to: '/money/insights', label: 'Insights' }}>
        Health score
      </CardTitle>

      <div className="mt-4 flex-1">
        {loading && <LoadingRows rows={3} />}

        {!loading && error && notComputedYet && (
          <Empty
            message="No score yet — it needs a month with some spending in it."
            action={{ to: '/money/transactions', label: 'Add a transaction' }}
          />
        )}
        {!loading && error && !notComputedYet && (
          <ErrorState what="the health score" onRetry={reload} />
        )}

        {!loading && !error && data && <Score health={data} />}
      </div>
    </Card>
  );
}

function Score({ health }: { health: HealthScore }) {
  const { label, tone } = band(health.score);
  const { inputs } = health;
  const pacePct = Math.round((inputs.pace_ratio - 1) * 100);
  const components = [...(inputs.components ?? [])]
    .sort((a, b) => a.points / Math.max(a.max, 1) - b.points / Math.max(b.max, 1))
    .slice(0, 2);

  return (
    <>
      <div className="flex items-end gap-3">
        <p className="font-display text-5xl leading-none font-semibold text-ink tabular-nums">
          {health.score}
        </p>
        <p className="pb-1 text-sm text-ink-faint">/ 100</p>
        <span className="pb-1.5">
          <Pill tone={tone}>{label}</Pill>
        </span>
      </div>

      <p className="mt-3 text-sm text-ink-soft">
        {money(inputs.total_spent)} spent against {money(inputs.total_caps)} in caps.{' '}
        {inputs.categories_over_cap > 0
          ? `${inputs.categories_over_cap} of ${inputs.categories_total} categories over.`
          : 'Nothing over cap.'}
      </p>

      {Number.isFinite(inputs.pace_ratio) && inputs.pace_ratio > 0 && (
        <p className="mt-1 text-sm text-ink-soft">
          {pacePct > 2
            ? `Spending about ${pacePct}% faster than the month is passing.`
            : pacePct < -2
              ? `Spending about ${Math.abs(pacePct)}% slower than the month is passing.`
              : 'Spending is keeping pace with the month.'}
        </p>
      )}

      {components.length > 0 && (
        <dl className="mt-4 space-y-2 border-t border-border pt-4">
          {components.map((c) => (
            <div key={c.label} className="flex items-baseline justify-between gap-3">
              <dt className="text-sm text-ink">{c.label}</dt>
              <dd className="text-sm text-ink-soft tabular-nums">
                {c.points} / {c.max}
              </dd>
            </div>
          ))}
        </dl>
      )}

      <p className="mt-4 text-xs text-ink-faint">
        Illustrative only — a summary of our own numbers, not financial advice.
      </p>
    </>
  );
}
